// CONTRACT:I4-POLICY-DECISION.1.0
/** Marking validation — detects INVALID marking COMBINATIONS, independent of any
 *  subject. This is a policy-hygiene check over the object's own markings (does
 *  this banner make sense?), distinct from the ABAC access decision (may YOU see
 *  it?). Grounded in the canonical rules:
 *   - TDFBot-Dataset-Intelligence/MARKING-GUIDE.md (the corpus marking law)
 *   - tdflite-defense-platform-configs/reference/classification-model.md
 *   - CAPCO / EO 13526 / ICD 710 marking conventions.
 *
 *  Severity:
 *   - error   → a CONTRADICTION (e.g. NOFORN + REL TO foreign). The PDP treats an
 *               object with any error as fail-closed (hidden), never shown in the
 *               clear, and flags it.
 *   - warning → unusual / likely-data-error, surfaced but not blocking.
 *
 *  Markings can be encoded differently across datasets (SCI in `caveats` as bare
 *  `SI`, or in `mission` as `sci:SI` / `compartment:SI-G`), so we NORMALISE first:
 *  strip known prefixes and categorise each token by the policy bundle. */

import type { PolicyBundle } from "./types.ts";
import type { SecurityView } from "./fqn.ts";
import { CLASS_ORDER } from "./policy.ts";

export type Severity = "error" | "warning";

export interface MarkingViolation {
  /** Stable rule id (for tests / reports). */
  rule: string;
  severity: Severity;
  message: string;
}

export interface MarkingValidation {
  /** false iff there is at least one `error`-severity violation. */
  valid: boolean;
  violations: MarkingViolation[];
  /** The markings categorised by attribute (for transparency / the inspector). */
  categorized: Record<string, string[]>;
}

const FOREIGN_PARTNERS = ["GBR", "CAN", "AUS", "NZL"];
const FIVE_EYES = ["USA", "GBR", "CAN", "AUS", "NZL"];
/** Dissemination controls authorized ONLY for CLASSIFIED national intelligence
 *  (ICD 710 / DoDM 5200.01 Vol 2). They CANNOT mark plain UNCLASSIFIED. PROPIN
 *  (proprietary info — may be unclassified) and FOUO (itself an unclassified control)
 *  are deliberately EXCLUDED: they are NOT classified-only. Exported so the COP banner
 *  module (`apps/cop/src/components/dod-marking.ts`) mirrors the SAME set under a drift
 *  test — the banner must never PAINT a classified-only control on an UNCLASSIFIED line. */
export const CLASSIFIED_ONLY_DISSEM = new Set(["NOFORN", "ORCON", "IMCON", "RELIDO"]);
/** Marking tokens may be prefixed in some datasets (e.g. mission `sci:SI`). */
const TOKEN_PREFIX = /^(sci|compartment|sap|accm|dissem|control|rel)\s*:/i;

const up = (s: string): string => String(s).trim().toUpperCase();
const cleanToken = (t: string): string => up(String(t).replace(TOKEN_PREFIX, ""));

/** Which policy attribute lists this value (case-insensitive), or null. */
function attrForValue(value: string, bundle: PolicyBundle): string | null {
  const lc = value.toLowerCase();
  const def = bundle.attributes.find((a) => a.values.some((x) => x.toLowerCase() === lc));
  return def ? def.name : null;
}

/** Validate an object's markings for internal coherence. */
export function validateMarkings(security: SecurityView, bundle: PolicyBundle): MarkingValidation {
  const violations: MarkingViolation[] = [];
  const byAttr: Record<string, string[]> = {};
  const add = (attr: string, val: string): void => {
    (byAttr[attr] ??= []).push(up(val));
  };
  const err = (rule: string, message: string): void => {
    violations.push({ rule, severity: "error", message });
  };
  const warn = (rule: string, message: string): void => {
    violations.push({ rule, severity: "warning", message });
  };

  // --- Classification (required, must be a legal level) ---
  // The classification ladder is the HIERARCHY-rule attribute (`classification` in the
  // single canonical defense.local model). CLASS_ORDER is the one shared ladder.
  const clsDef = bundle.attributes.find((a) => a.rule === "hierarchy");
  const clsRaw = security.classification;
  if (!clsRaw) {
    err("MISSING_CLASSIFICATION", "object has no classification");
  } else if (!clsDef || !clsDef.values.some((v) => v.toLowerCase() === clsRaw.toLowerCase())) {
    err("ILLEGAL_CLASSIFICATION", `"${clsRaw}" is not a legal classification value`);
  } else {
    add(clsDef.name, clsRaw);
  }
  const cls = up(clsRaw ?? "");
  // "Classified" here means above the UNCLASSIFIED floor (CUI and up) — drives the
  // dissemination-scope coherence warnings below.
  const classified = (CLASS_ORDER[cls] ?? 0) >= 1;

  // --- Releasability tokens → the releasability/coalition attribute (one canonical
  //     `releasability` axis now). Legal if the value belongs to any non-classification attr. ---
  for (const r of security.releasability ?? []) {
    const attr = attrForValue(r, bundle);
    if (attr && attr !== clsDef?.name) add(attr, r);
    else err("ILLEGAL_RELEASABILITY", `releasability "${r}" is not a legal REL TO / coalition value`);
  }

  // --- Caveats are markings → must categorise (else illegal). Mission may carry
  //     SCI markings (prefixed) OR be a free-text op name (ignored if unmatched). ---
  for (const c of security.caveats ?? []) {
    const tok = cleanToken(c);
    const attr = attrForValue(tok, bundle);
    if (attr && attr !== clsDef?.name) add(attr, tok);
    else err("ILLEGAL_CAVEAT", `caveat "${c}" is not a legal marking value`);
  }
  for (const m of security.mission ?? []) {
    const tok = cleanToken(m);
    const attr = attrForValue(tok, bundle);
    if (attr && attr !== clsDef?.name) add(attr, tok);
    // unmatched mission tokens are operational names (e.g. "DEMO") — not markings.
  }

  // Cross-rules are VALUE/ROLE-based: we key off the marking VALUES + attribute ROLES
  // (not hardcoded attribute names) so the contradiction checks stay correct over the
  // canonical model — NOFORN lives under handling_caveat, REL under releasability.
  const allVals = Object.values(byAttr).flat();
  const relValues = (security.releasability ?? []).map(up);
  const noforn = allVals.includes("NOFORN");
  const foreignRel = relValues.filter((r) => FOREIGN_PARTNERS.includes(r));
  const coalition = allVals.filter((v) => v === "FVEY" || v === "NATO");
  // Compartment/program markings, identified by attribute ROLE (works on either bundle).
  const valsOfRole = (re: RegExp): string[] =>
    Object.keys(byAttr).filter((n) => re.test(n)).flatMap((a) => byAttr[a] ?? []);
  const sciSapVals = valsOfRole(/sci|sap|accm|compartment|program/i);
  const sciVals = valsOfRole(/sci|compartment/i);
  const controlVals = valsOfRole(/control/i);
  const dissemVals = allVals.filter((v) => ["ORCON", "PROPIN", "IMCON", "RELIDO", "FOUO"].includes(v));

  // R1 NOFORN ⊥ REL TO foreign partner (the load-bearing exclusivity).
  if (noforn && foreignRel.length) {
    err("NOFORN_WITH_FOREIGN_REL", `NOFORN is mutually exclusive with REL TO foreign partners (${foreignRel.join(", ")})`);
  }
  // R2 NOFORN ⊥ coalition disclosure (FVEY/NATO).
  if (noforn && coalition.length) {
    err("NOFORN_WITH_COALITION_DISCLOSURE", `NOFORN is mutually exclusive with coalition disclosure (${coalition.join(", ")})`);
  }
  // R3 A sub-compartment (one with a parent prefix, e.g. SI-G ⇒ SI, TK-RUFF ⇒ TK)
  //    requires its parent control system. A BARE compartment value (e.g. `SI` in
  //    defense.local, where SI is itself the compartment) has no parent — skip it.
  for (const comp of sciVals) {
    if (!comp.includes("-")) continue;
    const parent = comp.split("-")[0];
    // Coherent iff the parent is present — either as a control system (tdfbot.local)
    // or as the parent compartment itself (defense.local lists SI and SI-G siblings).
    if (!controlVals.includes(parent) && !sciVals.includes(parent)) {
      err("COMPARTMENT_WITHOUT_CONTROL", `compartment ${comp} requires its parent control system / compartment ${parent}`);
    }
  }
  // R4 UNCLASSIFIED cannot carry SCI/SAP/ACCM (classified-only markings).
  if (cls === "UNCLASSIFIED") {
    if (sciSapVals.length) {
      err("UNCLASS_WITH_SCI_SAP", `UNCLASSIFIED cannot carry SCI/SAP/ACCM markings (${sciSapVals.join(", ")})`);
    }
    // R4b CONTRADICTION (hardened from warning → error 2026-06-29): the classified-only
    // dissemination controls (NOFORN/ORCON/IMCON/RELIDO) are authorized only for CLASSIFIED
    // national intelligence and CANNOT mark plain UNCLASSIFIED → fail closed. This block is
    // gated on STRICT UNCLASSIFIED, so CUI//NOFORN (NOFORN as a CUI Limited-Dissemination
    // Control) stays valid. PROPIN (may be unclassified) and FOUO (an unclassified control)
    // are NOT classified-only → they remain an "unusual" warning, never blocking.
    const classifiedOnly = allVals.filter((v) => CLASSIFIED_ONLY_DISSEM.has(v));
    if (classifiedOnly.length) {
      err(
        "UNCLASS_WITH_CLASSIFIED_DISSEM",
        `UNCLASSIFIED cannot carry classified-only dissemination controls (${classifiedOnly.join(", ")}) — they mark CLASSIFIED national intelligence`,
      );
    }
    const unusualDissem = dissemVals.filter((v) => !CLASSIFIED_ONLY_DISSEM.has(v));
    if (unusualDissem.length) {
      warn("UNCLASS_WITH_CAVEAT", `UNCLASSIFIED with a ${unusualDissem.join(", ")} caveat is unusual`);
    }
  }
  // R5 (warn) SCI present but not TOP_SECRET (TS/SCI convention).
  if (sciVals.length && cls !== "TOP_SECRET") {
    warn("SCI_BELOW_TS", `SCI (${sciVals.join(", ")}) on a ${cls} object — TS/SCI is conventionally TOP_SECRET`);
  }
  // R6 (warn) FVEY disclosure but REL TO not the full Five-Eyes list.
  if (coalition.includes("FVEY")) {
    const missing = FIVE_EYES.filter((c) => !relValues.includes(c));
    if (missing.length) warn("FVEY_INCOMPLETE_REL", `FVEY disclosure but REL TO missing ${missing.join(", ")}`);
  }
  // R7 (warn) classified REL TO without USA (US-originated convention).
  if (classified && relValues.length && !relValues.includes("USA")) {
    warn("REL_WITHOUT_USA", "REL TO present without USA (US-originated marking convention)");
  }
  // R8 (warn) classified object with NO dissemination scope at all (ambiguous).
  if (classified && !noforn && !relValues.length && !coalition.length) {
    warn("NO_DISSEM_SCOPE", `${cls} object has no NOFORN / REL TO / coalition scope — ambiguous releasability`);
  }

  return {
    valid: !violations.some((v) => v.severity === "error"),
    violations,
    categorized: byAttr,
  };
}
